import { useState, useEffect, useRef } from "react";
import { apiFetch } from "../../../context/AuthContext";

export const useHolidays = ({ profile, language, t, onAlert }) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const [holidayList, setHolidayList] = useState([]);
  const [holidayLoading, setHolidayLoading] = useState(false);
  const [holidayClientsList, setHolidayClientsList] = useState([]);
  const [holidayCompanies, setHolidayCompanies] = useState([]);

  const [holidayDepartments, setHolidayDepartments] = useState([]);
  const [deptPickerOpen, setDeptPickerOpen] = useState(false);

  const EMPTY_FORM = {
    name: "",
    date: "",
    clientName: "",
    workingCompany: "",
    selectedDepartments: [],
    allDepartments: false,
  };

  const [holidayForm, setHolidayForm] = useState(EMPTY_FORM);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidaySubmitting, setHolidaySubmitting] = useState(false);
  const [holidayDeleteConfirmId, setHolidayDeleteConfirmId] = useState(null);
  const [holidayClientFilter, setHolidayClientFilter] = useState("");
  const [holidayCompanyFilter, setHolidayCompanyFilter] = useState("");
  const [holidaySearch, setHolidaySearch] = useState("");

  /* ── Ref is updated SYNCHRONOUSLY inside the setter, not via useEffect ── */
  const holidayFormRef = useRef(EMPTY_FORM);

  const setForm = (updater) => {
    setHolidayForm((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      holidayFormRef.current = next;          // always in sync, same tick
      return next;
    });
  };

  /* ─────────────────────────────────────────────
     Fetch helpers
  ───────────────────────────────────────────── */

  const fetchHolidays = async () => {
    setHolidayLoading(true);
    try {
      const params = new URLSearchParams();
      if (holidayClientFilter)  params.set("clientName",     holidayClientFilter);
      if (holidayCompanyFilter) params.set("workingCompany", holidayCompanyFilter);
      if (holidaySearch)        params.set("search",         holidaySearch);
      const resp = await apiFetch(
        `${BASE_URL}/api/leaves/manage/holidays?${params.toString()}`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();
      setHolidayList(result.data || []);
    } catch (err) {
      console.error("fetchHolidays error:", err);
      setHolidayList([]);
    } finally {
      setHolidayLoading(false);
    }
  };

  const fetchClientsForHoliday = async () => {
    try {
      const resp = await apiFetch(
        `${BASE_URL}/employees/clients/all`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();
      let clients = result.data || result.clients || result;
      if (clients && typeof clients === "object" && !Array.isArray(clients)) clients = [clients];
      if (!Array.isArray(clients)) clients = [];
      const normalized = clients
        .map((c) => {
          if (typeof c === "string") return { clientName: c, clientCode: "" };
          const name = (c.clientName || c.name || "").toString().trim();
          if (!name) return null;
          return { clientName: name, clientCode: c.clientCode || "" };
        })
        .filter(Boolean);
      setHolidayClientsList(normalized);
    } catch (err) {
      console.error("fetchClientsForHoliday error:", err);
      setHolidayClientsList([]);
    }
  };

  const fetchCompaniesForHoliday = async (selectedClient) => {
    if (!selectedClient) { setHolidayCompanies([]); setHolidayDepartments([]); return; }
    try {
      const resp = await apiFetch(
        `${BASE_URL}/api/company-config/${encodeURIComponent(selectedClient)}/detect-companies`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();
      const safeCompanyName = (c) => {
        if (typeof c === "string") return c.trim();
        return (typeof (c?.companyName ?? c?.name) === "string"
          ? (c?.companyName ?? c?.name)
          : String(c?.companyName ?? c?.name ?? "")
        ).trim();
      };
      const src = result.success && result.data?.companies
        ? result.data.companies
        : result.data && Array.isArray(result.data)
          ? result.data
          : Array.isArray(result) ? result : [];
      const companies = src
        .map((c) => ({ name: safeCompanyName(c), companyName: safeCompanyName(c) }))
        .filter((c) => c.name);
      setHolidayCompanies(companies);
    } catch (err) {
      console.error("fetchCompaniesForHoliday error:", err);
      setHolidayCompanies([]);
    }
  };

  const fetchDepartmentsForHoliday = async (clientName, workingCompany) => {
    console.log("🔍 fetchDepartmentsForHoliday:", { clientName, workingCompany });
    if (!clientName || !workingCompany) { setHolidayDepartments([]); return; }
    try {
      // Departments come from /api/departments (departments:view OR employees:*)
      // instead of the company-config lookup, so Holiday Management works
      // without a configurations grant (switched 2026-08-10).
      const resp = await apiFetch(
        `${BASE_URL}/api/departments?clientName=${encodeURIComponent(clientName)}&workingCompany=${encodeURIComponent(workingCompany)}`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();
      const departments = result?.data;
      const names = (Array.isArray(departments) ? departments : [])
        .map((d) => typeof d === "string" ? d.trim() : String(d?.name ?? "").trim())
        .filter(Boolean);
      setHolidayDepartments([...new Set(names)]);
    } catch (err) {
      console.error("fetchDepartmentsForHoliday error:", err);
      setHolidayDepartments([]);
    }
  };

  /* ─────────────────────────────────────────────
     Modal open / form helpers
  ───────────────────────────────────────────── */

  const openHolidayModal = () => {
    const adminClient = profile?.clientName || localStorage.getItem("clientName") || "";
    setForm({ ...EMPTY_FORM, clientName: adminClient });
    setHolidayClientFilter("");
    setHolidayCompanyFilter("");
    setHolidaySearch("");
    setEditingHoliday(null);
    setHolidayDepartments([]);
    fetchCompaniesForHoliday(adminClient);
    fetchHolidays();
    fetchClientsForHoliday();
  };

  const handleHolidayFormChange = (field, value) => {
    if (field === "clientName") {
      setForm((prev) => ({
        ...prev,
        clientName: value,
        workingCompany: "",
        selectedDepartments: [],
        allDepartments: false,
      }));
      setHolidayDepartments([]);
      fetchCompaniesForHoliday(value);

    } else if (field === "workingCompany") {
      /* holidayFormRef.current is always current because setForm updates it
         synchronously inside the state setter — no stale closure possible */
      const currentClientName = holidayFormRef.current.clientName;
      setForm((prev) => ({
        ...prev,
        workingCompany: value,
        selectedDepartments: [],
        allDepartments: false,
      }));
      fetchDepartmentsForHoliday(currentClientName, value);

    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  /* ── department picker ── */
  const handleOpenDeptPicker  = () => setDeptPickerOpen(true);
  const handleCloseDeptPicker = () => setDeptPickerOpen(false);

  const handleDeptPickerConfirm = ({ allDepartments, selectedDepartments }) => {
    setForm((prev) => ({ ...prev, allDepartments, selectedDepartments }));
  };

  /* ─────────────────────────────────────────────
     Edit / Cancel
  ───────────────────────────────────────────── */

  const handleStartEditHoliday = (holiday) => {
    setEditingHoliday(holiday);
    const dateStr = holiday.date ? new Date(holiday.date).toISOString().split("T")[0] : "";
    const adminClient = profile?.clientName || localStorage.getItem("clientName") || holiday.clientName || "";
    setForm({
      name:                holiday.name           || "",
      date:                dateStr,
      clientName:          adminClient,
      workingCompany:      holiday.workingCompany  || "",
      selectedDepartments: holiday.departments     || [],
      allDepartments:      holiday.allDepartments  ?? false,
    });
    fetchDepartmentsForHoliday(adminClient, holiday.workingCompany);
  };

  const handleCancelEditHoliday = () => {
    setEditingHoliday(null);
    setForm(EMPTY_FORM);
    setHolidayCompanies([]);
    setHolidayDepartments([]);
  };

  /* ─────────────────────────────────────────────
     Submit
  ───────────────────────────────────────────── */

  const handleSubmitHoliday = async () => {
    const form = holidayFormRef.current;

    if (!form.name || !form.date || !form.clientName || !form.workingCompany) {
      onAlert({
        open: true,
        title:   language === "ar" ? "خطأ"  : "Error",
        message: language === "ar"
          ? "يرجى ملء جميع الحقول المطلوبة"
          : "Please fill in all required fields",
        variant: "error",
      });
      return;
    }

    setHolidaySubmitting(true);
    try {
      const payload = {
        name:           form.name,
        date:           form.date,
        clientName:     form.clientName,
        workingCompany: form.workingCompany,
        allDepartments: form.allDepartments,
        departments:    form.allDepartments ? [] : form.selectedDepartments,
      };

      let resp;
      if (editingHoliday) {
        resp = await apiFetch(
          `${BASE_URL}/api/leaves/manage/holidays/${editingHoliday.id}`,
          { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }
        );
      } else {
        resp = await apiFetch(
          `${BASE_URL}/api/leaves/manage/holidays`,
          { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }
        );
      }

      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${resp.status}`);
      }

      const result = await resp.json();
      const saved  = result.data;

      if (editingHoliday) {
        setHolidayList((prev) => prev.map((h) => (h.id === saved.id ? saved : h)));
        onAlert({ open: true, title: language === "ar" ? "نجاح" : "Success", message: t.holidayUpdated, variant: "success" });
        setEditingHoliday(null);
        setForm(EMPTY_FORM);
        setHolidayCompanies([]);
        setHolidayDepartments([]);
        return true;
      } else {
        setHolidayList((prev) => [saved, ...prev]);
        onAlert({ open: true, title: language === "ar" ? "نجاح" : "Success", message: t.holidayCreated, variant: "success" });
        const currentClient  = form.clientName;
        const currentCompany = form.workingCompany;
        setForm({ ...EMPTY_FORM, clientName: currentClient, workingCompany: currentCompany });
        fetchCompaniesForHoliday(currentClient);
        fetchDepartmentsForHoliday(currentClient, currentCompany);
        return false;
      }
    } catch (err) {
      onAlert({ open: true, title: language === "ar" ? "خطأ" : "Error", message: err.message, variant: "error" });
      return false;
    } finally {
      setHolidaySubmitting(false);
    }
  };

  /* ─────────────────────────────────────────────
     Delete
  ───────────────────────────────────────────── */

  const handleDeleteHoliday = async (id) => {
    try {
      const resp = await apiFetch(
        `${BASE_URL}/api/leaves/manage/holidays/${id}`,
        { method: "DELETE", headers: { "Content-Type": "application/json" } }
      );
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${resp.status}`);
      }
      setHolidayList((prev) => prev.filter((h) => h.id !== id));
      setHolidayDeleteConfirmId(null);
      onAlert({ open: true, title: language === "ar" ? "نجاح" : "Success", message: t.holidayDeleted, variant: "success" });
    } catch (err) {
      onAlert({ open: true, title: language === "ar" ? "خطأ" : "Error", message: err.message, variant: "error" });
    }
  };

  /* ─────────────────────────────────────────────
     Re-fetch on filter change
  ───────────────────────────────────────────── */

  useEffect(() => {
    fetchHolidays();
  }, [holidayClientFilter, holidayCompanyFilter, holidaySearch]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─────────────────────────────────────────────
     Public API
  ───────────────────────────────────────────── */

  return {
    holidayList,
    holidayLoading,
    holidayClientsList,
    holidayCompanies,
    holidayDepartments,
    holidayForm,
    editingHoliday,
    holidaySubmitting,
    holidayDeleteConfirmId,
    setHolidayDeleteConfirmId,
    holidayClientFilter,
    setHolidayClientFilter,
    holidayCompanyFilter,
    setHolidayCompanyFilter,
    holidaySearch,
    setHolidaySearch,
    deptPickerOpen,
    handleOpenDeptPicker,
    handleCloseDeptPicker,
    handleDeptPickerConfirm,
    openHolidayModal,
    handleHolidayFormChange,
    handleStartEditHoliday,
    handleCancelEditHoliday,
    handleSubmitHoliday,
    handleDeleteHoliday,
  };
};